import { useCallback, useEffect, useRef, useState } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { JitLogo } from '@/brand/JitLogo';
import { Alert, type AlertTone } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { ThemeToggle } from '@/theme/ThemeToggle';
import { login, verifyTotp } from '@/lib/api/endpoints/auth';
import { isApiError } from '@/lib/api/errors';
import { API_TRANSPORT } from '@/lib/api/config';
import { useAuth } from '@/lib/auth/AuthProvider';

/*
  Sign in.

  Two screens, not one form with a hidden field. The password leg and the second factor leg
  are separate server calls (docs/07-identity-rbac.md I.1), the second one holds an opaque
  mfaToken rather than the password, and making that visible in the UI keeps the two
  credentials from ever being in the same form state at the same time.

  ERROR COPY IS A SECURITY SURFACE. Read this before editing any string below.

  I.1 requires that responses not confirm whether an account exists. So:

  * A wrong password, an unknown email, a disabled account and a suspended account all
    render the same message: "Those sign in details were not accepted."
  * The locked message says the account is temporarily locked WITHOUT naming the address,
    and the server returns it whether or not the address exists, so reading it tells an
    attacker nothing. The wording avoids "your account" for the same reason.
  * Nothing echoes the submitted email back into an error string.
  * A network failure is worded so it cannot be mistaken for a rejection. "Could not reach"
    is a different claim from "not accepted", and conflating them sends technicians hunting
    for a password problem during an outage.

  Every one of the five required states is reachable and visibly distinct: invalid
  credentials, account locked, MFA required (the whole second screen), MFA invalid, and
  network failure.
*/

type Step =
  | { readonly kind: 'credentials' }
  | { readonly kind: 'totp'; readonly mfaToken: string };

interface Feedback {
  readonly tone: AlertTone;
  readonly title: string;
  readonly detail?: string;
}

const GENERIC_REJECTION: Feedback = {
  tone: 'danger',
  title: 'Those sign in details were not accepted',
  // Deliberately says nothing about which part was wrong, or whether the address is known.
  detail: 'Check the email address and password and try again.',
};

const NETWORK_FAILURE: Feedback = {
  tone: 'offline',
  title: 'Could not reach the JIT server',
  detail:
    'Nothing was submitted. This is a connection problem, not a rejected password. Check your network, then try again.',
};

const UNEXPECTED: Feedback = {
  tone: 'danger',
  title: 'Sign in could not be completed',
  detail: 'The server returned something this console did not expect. Try again in a moment.',
};

function lockedFeedback(lockedUntil: string | undefined): Feedback {
  const minutes =
    lockedUntil === undefined
      ? null
      : Math.max(1, Math.round((Date.parse(lockedUntil) - Date.now()) / 60_000));
  return {
    tone: 'locked',
    title: 'Sign in is temporarily locked',
    detail:
      minutes === null
        ? 'Too many failed attempts from this account or address. Wait for the lock to expire, or ask an administrator to clear it.'
        : `Too many failed attempts. Try again in about ${String(minutes)} minutes, or ask an administrator to clear the lock.`,
  };
}

export function LoginPage(): React.JSX.Element {
  const { onSignedIn } = useAuth();
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // Moving focus to the code field is the difference between the second screen being
  // usable from the keyboard and being a dead end.
  useEffect(() => {
    if (step.kind === 'totp') {
      codeInputRef.current?.focus();
    }
  }, [step.kind]);

  const handleFailure = useCallback((error: unknown): void => {
    if (!isApiError(error)) {
      setFeedback(UNEXPECTED);
      return;
    }
    switch (error.kind) {
      case 'network':
        setFeedback(NETWORK_FAILURE);
        return;
      case 'account_locked':
        setFeedback(lockedFeedback(error.problem.lockedUntil));
        return;
      case 'invalid_credentials':
        setFeedback(GENERIC_REJECTION);
        return;
      case 'mfa_invalid':
        setFeedback({
          tone: 'danger',
          title: 'That code was not accepted',
          detail:
            'Authenticator codes last 30 seconds and each one can only be used once. Wait for the next code and enter it.',
        });
        setFieldError('Incorrect or already used code');
        return;
      case 'rate_limited':
        setFeedback({
          tone: 'warning',
          title: 'Too many attempts',
          detail: 'Slow down and try again shortly.',
        });
        return;
      case 'unauthenticated':
        // The mfaToken expired between the two legs. Send them back rather than leaving
        // them on a screen whose token is dead.
        setStep({ kind: 'credentials' });
        setPassword('');
        setCode('');
        setFeedback({
          tone: 'warning',
          title: 'That sign in attempt expired',
          detail: 'Start again from your email address and password.',
        });
        return;
      default:
        setFeedback(UNEXPECTED);
        return;
    }
  }, []);

  async function submitCredentials(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setFeedback(null);
    setFieldError(undefined);
    try {
      const result = await login(email, password);
      if (result.kind === 'mfa_required') {
        setStep({ kind: 'totp', mfaToken: result.mfaToken });
        setPassword('');
      } else {
        await onSignedIn();
      }
    } catch (error) {
      handleFailure(error);
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (step.kind !== 'totp') {
      return;
    }
    setBusy(true);
    setFeedback(null);
    setFieldError(undefined);
    try {
      const result = await verifyTotp(step.mfaToken, code);
      if (result.kind === 'signed_in') {
        await onSignedIn();
      } else {
        setFeedback(UNEXPECTED);
      }
    } catch (error) {
      handleFailure(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-dvh flex-col bg-canvas">
      {/* Decorative wash in the brand gradient. Behind everything, no contrast impact. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-80 opacity-[0.16]"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 0%, var(--jit-blue-300), transparent 70%)',
        }}
      />

      <div className="relative flex justify-end p-4">
        <ThemeToggle />
      </div>

      <div className="relative flex flex-1 items-start justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="mb-7 flex flex-col items-center gap-3 text-center">
            <JitLogo height={40} />
            <p className="text-sm text-fg-muted">Technician console</p>
          </div>

          <div className="rounded-[var(--radius-panel)] border border-border bg-surface p-6 shadow-lg">
            {step.kind === 'credentials' ? (
              <>
                <h1 className="text-lg font-semibold text-fg">Sign in</h1>
                <p className="mt-1 mb-5 text-sm text-fg-muted">
                  Your authenticator code is asked for on the next screen.
                </p>

                {feedback !== null ? (
                  <Alert
                    tone={feedback.tone}
                    title={feedback.title}
                    liveness="assertive"
                    className="mb-4"
                  >
                    {feedback.detail}
                  </Alert>
                ) : null}

                <form
                  noValidate
                  onSubmit={(event) => {
                    void submitCredentials(event);
                  }}
                  className="flex flex-col gap-4"
                >
                  <Field
                    label="Work email"
                    type="email"
                    name="email"
                    autoComplete="username"
                    inputMode="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                  />
                  <Field
                    label="Password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={busy}
                    disabled={email.trim() === '' || password === ''}
                  >
                    <KeyRound className="size-4" aria-hidden="true" />
                    Continue
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h1 className="text-lg font-semibold text-fg">Two-factor authentication</h1>
                <p className="mt-1 mb-5 text-sm text-fg-muted">
                  Enter the six digit code from your authenticator app.
                </p>

                {feedback !== null ? (
                  <Alert
                    tone={feedback.tone}
                    title={feedback.title}
                    liveness="assertive"
                    className="mb-4"
                  >
                    {feedback.detail}
                  </Alert>
                ) : null}

                <form
                  noValidate
                  onSubmit={(event) => {
                    void submitTotp(event);
                  }}
                  className="flex flex-col gap-4"
                >
                  <Field
                    ref={codeInputRef}
                    label="Authenticator code"
                    name="otp"
                    // one-time-code lets the OS offer the code from a password manager or
                    // a paired phone instead of forcing a manual copy.
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9 ]*"
                    maxLength={7}
                    required
                    hint="Codes change every 30 seconds and each code works only once."
                    error={fieldError}
                    value={code}
                    onChange={(event) => {
                      setCode(event.target.value);
                      setFieldError(undefined);
                    }}
                    className="font-mono text-lg tracking-[0.35em]"
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={busy}
                    disabled={code.replace(/\D/g, '').length < 6}
                  >
                    <ShieldCheck className="size-4" aria-hidden="true" />
                    Verify and sign in
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setStep({ kind: 'credentials' });
                      setCode('');
                      setFeedback(null);
                      setFieldError(undefined);
                    }}
                    className="self-center"
                  >
                    Start again
                  </Button>
                </form>
              </>
            )}
          </div>

          {API_TRANSPORT === 'mock' ? (
            <div className="mt-5 rounded-[var(--radius-control)] border border-dashed border-border bg-surface-sunken p-3 text-xs leading-relaxed text-fg-muted">
              <p className="font-semibold text-fg">Fixture transport is active</p>
              <p className="mt-1">
                No server is involved. The accounts and the accepted values are listed at the
                top of <code className="font-mono">src/mocks/handlers.ts</code>, including the
                addresses that force the locked and unreachable states so each one can be
                checked.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
